import { useCallback, useContext, useMemo } from 'react';
import type { Chat } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ActionsContext, type ActionsContextValue, ActiveChatSessionContext } from '../Chat/context';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { useAttachmentContext } from './context';

function useBuildOpenNewSubjectForTableAction() {
  const dispatch = useContext(ChatStateDispatchContext);
  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  if (!brandIdentifier) {
    throw new Error('Brand identifier is required to build open new subject for table action');
  }

  return useCallback(
    (row: Chat.AttachmentTableRow, action: Chat.Attachment.SelectProfileItem) => () => {
      const hasIdField = action.idField && action.idField in row;
      const hasNameField = action.nameField && action.nameField in row;

      if (!hasIdField || !hasNameField) {
        return null;
      }

      const idField = action.idField ? row[action.idField] : undefined;
      const nameField = action.nameField ? row[action.nameField] : undefined;

      const idValue = typeof idField === 'object' ? idField.value : idField;
      const nameValue = typeof nameField === 'object' ? nameField.value : nameField;

      const subject = {
        id: idValue as string | number,
        name: nameValue,
      };
      const initialUserMessage = action.initialMessageTemplate
        ? action.initialMessageTemplate.replace('{name}', subject.name)
        : `Deep dive into ${subject.name}`;

      if (chat.context.type !== ChatContextType.Agent) {
        throw new Error('Cannot open new chat for subject when chat context is not Agent');
      }

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: {
            ...chat.context,
            data: {
              ...chat.context.data,
              subject,
            },
          },
          pendingQuery: initialUserMessage,
        },
      });
    },
    [dispatch, chat.context, brandIdentifier]
  );
}

const useRowLevelActions = (row: Chat.AttachmentTableRow): ActionsContextValue => {
  const tableAttachment = useAttachmentContext<Chat.Attachment.Table>();

  const tableActions = tableAttachment.data?.actions ?? {};

  const buildOpenNewSubjectForTableAction = useBuildOpenNewSubjectForTableAction();

  return useMemo(() => {
    const actions: ActionsContextValue = {};

    if (!tableActions) {
      return actions;
    }

    const actionKeys = Object.keys(tableActions ?? {});

    for (const actionKey of actionKeys) {
      const action = tableActions[actionKey];

      if (!action) {
        continue;
      }

      if (action.type === 'open-new-tab-for-subject') {
        const actionCallback = buildOpenNewSubjectForTableAction(row, action);

        if (!actionCallback) {
          continue;
        }
        actions[actionKey] = actionCallback;
      }
    }

    return actions;
  }, [tableActions, buildOpenNewSubjectForTableAction, row]);
};

type Props = {
  row: Chat.AttachmentTableRow;
  children: React.ReactNode;
};

export const TableActionsContainer = (props: Props) => {
  const actions = useRowLevelActions(props.row);

  return <ActionsContext.Provider value={actions}>{props.children}</ActionsContext.Provider>;
};
